import type {
  Category,
  ConsentKitConfig,
  Decision,
  Dictionary,
  LocaleCode,
  Regime,
  VendorDefinition,
} from "../types";
import { ALL_CATEGORIES } from "../core/categories";
import { vendorsForCategory } from "../vendors/registry";
import { h, on } from "./dom";

export interface PreferencesCallbacks {
  onSave(decision: Decision): void;
  onAcceptAll(): void;
  onRejectAll(): void;
  onClose(): void;
}

export interface PreferencesOptions {
  config: ConsentKitConfig;
  t: Dictionary;
  /** Already resolved — `config.locale` may still be "auto". */
  locale: LocaleCode;
  regime: Regime;
  vendors: VendorDefinition[];
  current: Decision;
}

export interface PreferencesView {
  overlay: HTMLElement;
  panel: HTMLElement;
}

/**
 * The second layer: per-category toggles plus the disclosure each category needs —
 * provider, concrete purpose, storage duration and whether data leaves the EU.
 * "Accept all" and "Reject all" repeat here so a user who opened settings is never
 * forced to hand-toggle their way out.
 *
 * The toggles render `current`, whatever it is. Under the DSGVO that means every optional
 * category is off until the visitor turns it on — nothing pre-ticked, CJEU C-673/17. Under
 * US law the same code shows them on, because that is the truth of what is running and a
 * dialog that lied about it would make the opt-out impossible to find.
 */
export function createPreferences(
  options: PreferencesOptions,
  callbacks: PreferencesCallbacks,
): PreferencesView {
  const { config, t, regime, vendors, current } = options;
  const toggles = new Map<Category, HTMLInputElement>();

  const overlay = h("div", { class: "ck-overlay" });
  const panel = h("div", {
    class: "ck-panel ck-panel--center",
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": "ck-prefs-title",
  });

  const close = h(
    "button",
    { type: "button", class: "ck-close", "aria-label": t.prefs.close },
    ["×"],
  );
  on(close, "click", callbacks.onClose);

  const shown = ALL_CATEGORIES.filter(
    (category) => category === "necessary" || config.categories.enabled.includes(category),
  );

  const body = h(
    "div",
    {},
    shown.map((category) => createCategoryRow(category, options, current, toggles)),
  );

  const save = h("button", { type: "button", class: "ck-btn ck-btn--ghost" }, [t.prefs.save]);
  const acceptAll = h("button", { type: "button", class: "ck-btn ck-btn--primary" }, [
    t.prefs.acceptAll,
  ]);
  const rejectAll = h("button", { type: "button", class: "ck-btn ck-btn--secondary" }, [
    t.prefs.rejectAll,
  ]);

  on(save, "click", () => {
    const decision: Decision = { necessary: true, functional: false, analytics: false, marketing: false };
    for (const [category, input] of toggles) decision[category] = input.checked;
    callbacks.onSave(decision);
  });
  on(acceptAll, "click", callbacks.onAcceptAll);
  on(rejectAll, "click", callbacks.onRejectAll);

  panel.append(
    h("div", { class: "ck-prefs-head" }, [
      h("h2", { class: "ck-title", id: "ck-prefs-title" }, [t.prefs.title]),
      close,
    ]),
    h("p", { class: "ck-body" }, [regime === "opt-out" ? t.us.prefsIntro : t.prefs.intro]),
    body,
    h("div", { class: "ck-actions" }, [rejectAll, acceptAll, save]),
  );

  return { overlay, panel };
}

function createCategoryRow(
  category: Category,
  options: PreferencesOptions,
  current: Decision,
  toggles: Map<Category, HTMLInputElement>,
): HTMLElement {
  const { t, regime, vendors } = options;
  const copy = t.categories[category];
  const locked = category === "necessary";

  // The necessary category's own description cites § 25 Abs. 2 TDDDG, which is not the law
  // a US visitor is being told about.
  const description =
    locked && regime === "opt-out" ? t.us.necessary : copy.description;

  const control = locked
    ? h("span", { class: "ck-always" }, [t.prefs.alwaysActive])
    : createToggle(category, copy.title, current[category], toggles);

  const matching = vendorsForCategory(vendors, category);

  return h("section", { class: "ck-cat" }, [
    h("div", { class: "ck-cat-head" }, [
      h("div", {}, [
        h("div", { class: "ck-cat-title" }, [copy.title]),
        h("div", { class: "ck-cat-desc" }, [description]),
      ]),
      control,
    ]),
    matching.length > 0 ? createVendorList(matching, options) : null,
  ]);
}

function createToggle(
  category: Category,
  label: string,
  checked: boolean,
  toggles: Map<Category, HTMLInputElement>,
): HTMLElement {
  const input = h("input", {
    type: "checkbox",
    "aria-label": label,
  }) as HTMLInputElement;
  // Set as a property, never as a `checked` attribute: no category may ever be
  // pre-ticked in markup (CJEU C-673/17), and this keeps that impossible by construction.
  input.checked = checked;
  toggles.set(category, input);

  return h("label", { class: "ck-toggle" }, [input, h("span", { class: "ck-track" })]);
}

function createVendorList(vendors: VendorDefinition[], options: PreferencesOptions): HTMLElement {
  const { t, locale, regime } = options;
  // Art. 13 Abs. 1 lit. f disclosure — the badge means "leaves the EEA", so it is dropped
  // rather than shown to a US visitor for whom the destination is home.
  const showTransfer = regime === "opt-in";

  return h(
    "div",
    { class: "ck-vendors" },
    vendors.map((vendor) =>
      h("div", { class: "ck-vendor" }, [
        h("div", {}, [
          h("span", { class: "ck-vendor-name" }, [vendor.name]),
          showTransfer && vendor.usTransfer
            ? h("span", { class: "ck-badge" }, [t.prefs.usTransfer])
            : null,
        ]),
        h("div", { class: "ck-vendor-row" }, [`${t.prefs.provider}: ${vendor.provider}`]),
        h("div", { class: "ck-vendor-row" }, [`${t.prefs.purpose}: ${vendor.purpose[locale]}`]),
        vendor.cookies.length > 0
          ? h("div", { class: "ck-vendor-row" }, [
              `${t.prefs.cookies}: ${vendor.cookies
                .map((cookie) => `${cookie.name} (${cookie.retention[locale]})`)
                .join(", ")}`,
            ])
          : null,
        h("div", { class: "ck-vendor-row" }, [
          h("a", { class: "ck-link", href: vendor.privacyUrl, target: "_blank", rel: "noopener" }, [
            t.prefs.privacyPolicy,
          ]),
        ]),
      ]),
    ),
  );
}
